/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from 'react'

type Language = 'en' | 'de'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const translations = {
  en: {
    // Navbar
    'nav.whyAzrefy': 'Why Azrefy?',
    'nav.features': 'Features',
    'nav.features.dispoPrevent': 'Dispo Prevention',
    'nav.features.charts': 'Charts & Analytics',
    'nav.features.aiTips': 'AI Tips',
    'nav.features.roastMode': 'Roast Mode',
    'nav.features.habitsScore': 'Money Habits Score',
    'nav.features.checkIn': 'Financial Check-In',
    'nav.features.points': 'AzrefyPoints',
    'nav.powerfulFeatures': 'Features',
    'nav.dispoEscape': 'DispoEscape',
    'nav.howItWorks': 'How it Works',
    'nav.about': 'About',
    'nav.contact': 'Contact',
    'nav.joinWaitlist': 'Join Waitlist',
    
    // Feature Navigation
    'features.navigation.previous': 'Previous Feature',
    'features.navigation.next': 'Next Feature',
    'features.navigation.progress': 'Feature {current} of {total}',
    
    // Hero
    'hero.launchingSoon': 'Launching Soon in Germany',
    'hero.badge.free': '100% Free Forever',
    'hero.badge.banks': 'All German Banks',
    'hero.badge.ai': 'AI Powered Insights',
    'hero.title.line1Start': 'Your ',
    'hero.title.line1AI': 'AI',
    'hero.title.line1End': ' Personal Financial Advisor.',
    'hero.title.line2': 'One Smart App.',
    'hero.title.line3': 'Real-time',
    'hero.title.line3AI': 'AI',
    'hero.title.line3End': 'Insights.',
    'hero.description': 'Connect your existing German bank account via our secure app. See beautiful real-time charts, get AI-powered money-saving tips, and stay financially secure.',
    'hero.banksSupport': 'We support all German banks:',
    'hero.joinWaitlist': 'Join the Waitlist',
    'hero.stats.banks': 'Banks',
    'hero.stats.realtime': 'Real-time',
    'hero.stats.updates': 'Updates',
    'hero.stats.bankLevel': 'Bank-level',
    'hero.stats.security': 'Security',
    
    // App Showcase
    'showcase.dispo.badge': 'Break Free',
    'showcase.dispo.title': 'Escape Dispo Kredit.',
    'showcase.dispo.titleGradient': 'Stay in control.',
    'showcase.dispo.description': 'Can\'t get out of your Dispo Kredit? Bank advisor only offering more credit? With Azrefy\'s AI tips, we\'ll guide you step by step to break free from this cycle permanently.',
    'showcase.dispo.callout.title': 'No Matter What You Try...',
    'showcase.dispo.callout.text': 'You can\'t seem to get out of your "Dispo Kredit," and your bank advisor isn\'t giving you a solution other than taking more credit? Don\'t worry — with our Azrefy tips, we\'ll guide you step by step to break free from this cycle. Our AI analyzes your spending patterns and provides personalized strategies to escape the overdraft trap permanently.',
    'showcase.dispo.feature1': 'AI-guided escape strategies',
    'showcase.dispo.feature2': 'Balance prediction & warnings',
    'showcase.dispo.feature3': 'Personalized action plans',
    'showcase.dispo.showDetails': 'Show More Details',
    
    // Dispo Escape Section
    'dispoEscape.cta': 'Get Started Free',
    
    // App Download Modal
    'appModal.title': 'App Coming Soon!',
    'appModal.subtitle': 'We\'re putting the finishing touches on Azrefy',
    'appModal.launchInfo': 'Our mobile app is currently in final development and will launch soon. Join our waitlist to be notified the moment we go live!',
    'appModal.launchTimeline': 'Expected Launch',
    'appModal.launchDate': 'Spring 2026',
    'appModal.platforms': 'Available on iOS & Android',
    'appModal.platformsDescription': 'Azrefy will be available for both iPhone and Android devices at launch.',
    'appModal.joinWaitlist': 'Join Waitlist for Early Access',
    'appModal.joinDescription': 'Be among the first to experience Azrefy. Get exclusive early access and special launch bonuses.',
    'appModal.notifyMe': 'Get notified when we launch',
    'appModal.earlyAccess': 'Early Access Benefits',
    'appModal.benefit1': 'First access to the app before public launch',
    'appModal.benefit2': 'Exclusive launch bonuses and AzrefyPoints',
    'appModal.benefit3': 'Priority support during your first month',
    'appModal.gotIt': 'Got It!',
    
    'showcase.charts.badge': 'Beautiful Analytics',
    'showcase.charts.title': 'See everything.',
    'showcase.charts.titleGradient': 'Understand everything.',
    'showcase.charts.description': 'Beautiful, interactive charts that show exactly where your money goes. Track spending by category, see trends over time, and get insights that matter.',
    'showcase.charts.feature1': 'Real-time spending charts',
    'showcase.charts.feature2': 'Category-based breakdown',
    'showcase.charts.feature3': 'Trend analysis & forecasts',
    'showcase.charts.showDetails': 'Show More Details',
    
    'showcase.tips.badge': 'AI-Powered',
    'showcase.tips.title': 'Smart money.',
    'showcase.tips.titleGradient': 'Smarter savings.',
    'showcase.tips.description': 'Get personalized AI tips multiple times a day. Discover duplicate subscriptions, find better deals, and learn how to save money effortlessly.',
    'showcase.tips.feature1': 'Daily AI-powered money tips',
    'showcase.tips.feature2': 'Duplicate subscription detection',
    'showcase.tips.feature3': 'Personalized savings goals',
    'showcase.tips.showDetails': 'Show More Details',
    
    'showcase.points.badge': 'Rewards Program',
    'showcase.points.title': 'Earn rewards.',
    'showcase.points.titleGradient': 'Save smarter.',
    'showcase.points.description': 'Get AzrefyPoints for every €10 you save. Progress through Bronze, Silver, and Gold tiers for better multipliers. Exchange points for premium features, gift coupons, or discount codes from trending shopping sites.',
    'showcase.points.feature1': 'Earn 1 point per €10 saved',
    'showcase.points.feature2': 'Tier multipliers up to 3×',
    'showcase.points.feature3': 'Goal milestone bonuses up to 1,000 pts',
    'showcase.points.showDetails': 'Show More Details',
    'showcase.points.feature4': 'Exchange for premium, coupons & discounts',
    
    'showcase.roast.badge': 'Brutally Honest',
    'showcase.roast.title': 'AI that roasts.',
    'showcase.roast.titleGradient': 'Because you need it.',
    'showcase.roast.description': 'Choose your AI personality: Professional, Friendly, or Roast Mode. Sometimes tough love is what you need to fix bad spending habits.',
    'showcase.roast.feature1': 'Three AI personalities to choose from',
    'showcase.roast.feature2': 'Roast Mode calls out bad habits',
    'showcase.roast.feature3': 'Real talk with actionable insights',
    'showcase.roast.showDetails': 'Show More Details',
    'showcase.roast.feature4': 'Switch anytime to match your mood',
    
    'showcase.habits.badge': 'Score-Based',
    'showcase.habits.title': 'Know your score.',
    'showcase.habits.titleGradient': 'Improve your habits.',
    'showcase.habits.description': 'Get a real-time Money Habits Score from 0-100. Track your financial health across savings, spending control, budgeting, and planning.',
    'showcase.habits.feature1': 'Live score updated daily (0-100)',
    'showcase.habits.feature2': 'Four key categories tracked',
    'showcase.habits.feature3': 'Achievement badges & milestones',
    'showcase.habits.feature4': 'Personalized improvement tips',
    'showcase.habits.showDetails': 'Show More Details',
    
    // Habits Score Modal
    'habitsScore.modal.title': 'Money Habits Score Explained',
    'habitsScore.modal.subtitle': 'How we calculate your financial health score',
    'habitsScore.modal.whatIs': 'What is the Money Habits Score?',
    'habitsScore.modal.intro': 'Your Money Habits Score is a comprehensive 0-100 point rating that evaluates your financial health across four key areas. Updated daily, it helps you track progress and build better money habits.',
    'habitsScore.modal.calculation': 'How It\'s Calculated',
    'habitsScore.modal.points': 'points',
    'habitsScore.modal.savingRate.title': 'Saving Rate',
    'habitsScore.modal.savingRate.description': 'Measures how much of your income you save each month.',
    'habitsScore.modal.savingRate.target': 'Target',
    'habitsScore.modal.savingRate.formula': 'Score = (savings / income) × 200',
    'habitsScore.modal.budgetAdherence.title': 'Budget Adherence',
    'habitsScore.modal.budgetAdherence.description': 'Evaluates how well you stick to your budget.',
    'habitsScore.modal.budgetAdherence.optimal': 'Optimal Range',
    'habitsScore.modal.budgetAdherence.good': '70-90% = Full points',
    'habitsScore.modal.budgetAdherence.overspend': 'Over 100% = Overspending',
    'habitsScore.modal.spendingConsistency.title': 'Spending Consistency',
    'habitsScore.modal.spendingConsistency.description': 'Tracks the stability of your spending patterns month-over-month.',
    'habitsScore.modal.spendingConsistency.stability': 'Pattern Stability',
    'habitsScore.modal.financialHealth.title': 'Financial Health',
    'habitsScore.modal.financialHealth.description': 'Rewards maintaining positive account balances.',
    'habitsScore.modal.financialHealth.positive': 'Positive Balance',
    'habitsScore.modal.distribution': 'Score Distribution',
    'habitsScore.modal.totalPoints': 'Total Points',
    'habitsScore.modal.tiers': 'Score Tiers',
    'habitsScore.modal.excellent': 'Excellent',
    'habitsScore.modal.good': 'Good',
    'habitsScore.modal.fair': 'Fair',
    'habitsScore.modal.needsWork': 'Needs Work',
    'habitsScore.modal.features': 'In-App Features',
    'habitsScore.modal.overview': 'Overview Tab',
    'habitsScore.modal.overviewFeature1': 'Real-time score with motivational messages',
    'habitsScore.modal.overviewFeature2': 'Quick stats dashboard',
    'habitsScore.modal.overviewFeature3': 'Personalized improvement tips',
    'habitsScore.modal.breakdown': 'Breakdown Tab',
    'habitsScore.modal.breakdownFeature1': 'Detailed component analysis',
    'habitsScore.modal.breakdownFeature2': 'Interactive pie chart',
    'habitsScore.modal.breakdownFeature3': 'Target vs. actual comparison',
    'habitsScore.modal.history': 'History Tab',
    'habitsScore.modal.historyFeature1': '6-month trend line chart',
    'habitsScore.modal.historyFeature2': 'Monthly score breakdown',
    'habitsScore.modal.historyFeature3': 'Progress tracking over time',
    'habitsScore.modal.cta': 'Ready to Improve Your Score?',
    'habitsScore.modal.ctaDescription': 'Download Azrefy and start tracking your Money Habits Score today. See your progress in real-time and get personalized tips to reach your financial goals.',
    'habitsScore.modal.gotIt': 'Got It!',
    
    // Dispo Prevention Modal
    'dispo.modal.title': 'Dispo Prevention System',
    'dispo.modal.subtitle': 'How we help you avoid overdraft fees',
    'dispo.modal.whatIs': 'What is Dispo Prevention?',
    'dispo.modal.intro': 'Our AI-powered system predicts when you might go into overdraft and helps you avoid expensive fees before they happen.',
    'dispo.modal.howItWorks': 'How It Works',
    'dispo.modal.step1.title': 'Pattern Analysis',
    'dispo.modal.step1.description': 'AI learns your spending patterns and income cycles to predict future balance accurately.',
    'dispo.modal.step2.title': 'Early Warnings',
    'dispo.modal.step2.description': 'Get alerts 3-7 days before your balance might go negative, giving you time to act.',
    'dispo.modal.step3.title': 'Smart Suggestions',
    'dispo.modal.step3.description': 'Receive personalized recommendations to prevent overdraft, from delaying purchases to finding extra income.',
    'dispo.modal.step4.title': 'Action Plans',
    'dispo.modal.step4.description': 'Step-by-step guides to escape existing overdraft situations and build a safety buffer.',
    'dispo.modal.whyCostly': 'Why Overdraft Is So Costly',
    'dispo.modal.cost1.title': 'High Interest Rates',
    'dispo.modal.cost1.description': 'Typical overdraft interest ranges from 8-15% APR, adding up quickly.',
    'dispo.modal.cost2.title': 'Daily Charges',
    'dispo.modal.cost2.description': 'Some banks charge per day you\'re overdrawn, making short-term overdrafts very expensive.',
    'dispo.modal.cost3.title': 'Cycle Trap',
    'dispo.modal.cost3.description': 'Once overdrawn, it\'s hard to recover as new charges push you deeper into negative territory.',
    'dispo.modal.aiHelps': 'How Our AI Helps',
    'dispo.modal.feature1.title': 'Balance Forecasting',
    'dispo.modal.feature1.description': 'Predicts your balance up to 30 days ahead based on recurring payments and spending habits.',
    'dispo.modal.feature2.title': 'Spending Alerts',
    'dispo.modal.feature2.description': 'Smart notifications when a purchase might push you into overdraft territory.',
    'dispo.modal.feature3.title': 'Escape Strategies',
    'dispo.modal.feature3.description': 'Customized plans to pay down overdraft efficiently while maintaining essential expenses.',
    'dispo.modal.cta': 'Take Control Today',
    'dispo.modal.ctaDescription': 'Join thousands who have escaped the overdraft trap with Azrefy\'s AI-powered prevention system.',
    'dispo.modal.gotIt': 'Got It!',
    
    // Charts & Analytics Modal
    'charts.modal.title': 'Charts & Analytics',
    'charts.modal.subtitle': 'Visualize your financial data',
    'charts.modal.whatIs': 'What Are These Charts?',
    'charts.modal.intro': 'Beautiful, interactive visualizations that transform your financial data into actionable insights. See patterns, trends, and opportunities at a glance.',
    'charts.modal.availableCharts': 'Available Chart Types',
    'charts.modal.pie.title': 'Pie Charts',
    'charts.modal.pie.description': 'See your spending distribution across categories. Quickly identify where most of your money goes.',
    'charts.modal.pie.example': 'Example: 40% Groceries, 25% Transport, 20% Entertainment, 15% Other',
    'charts.modal.line.title': 'Line Charts',
    'charts.modal.line.description': 'Track trends over time. See how your spending, income, and savings evolve month by month.',
    'charts.modal.line.example': 'Example: 6-month spending trend showing seasonal patterns',
    'charts.modal.bar.title': 'Bar Charts',
    'charts.modal.bar.description': 'Compare different categories or time periods side by side. Perfect for monthly comparisons.',
    'charts.modal.bar.example': 'Example: Compare this month vs last month across all categories',
    'charts.modal.trend.title': 'Trend Analysis',
    'charts.modal.trend.description': 'Automatic detection of upward or downward trends with predictive insights.',
    'charts.modal.trend.example': 'Example: "Your grocery spending increased 15% this month"',
    'charts.modal.insights': 'Smart Insights',
    'charts.modal.insight1.title': 'Time-Based',
    'charts.modal.insight1.description': 'Daily, weekly, monthly, or yearly views to spot patterns at any timeframe.',
    'charts.modal.insight2.title': 'Category Deep-Dive',
    'charts.modal.insight2.description': 'Drill down into any spending category to see transactions and sub-categories.',
    'charts.modal.insight3.title': 'Forecasting',
    'charts.modal.insight3.description': 'AI-powered predictions showing expected spending for upcoming months.',
    'charts.modal.customization': 'Customization Options',
    'charts.modal.custom1.title': 'Date Range Filters',
    'charts.modal.custom1.description': 'Select any date range from last week to last year.',
    'charts.modal.custom2.title': 'Category Selection',
    'charts.modal.custom2.description': 'Show or hide specific categories to focus on what matters.',
    'charts.modal.custom3.title': 'Export & Share',
    'charts.modal.custom3.description': 'Download charts as images or PDFs for your records.',
    'charts.modal.cta': 'Start Visualizing Your Finances',
    'charts.modal.ctaDescription': 'Beautiful charts and powerful analytics are waiting for you in the Azrefy app.',
    'charts.modal.gotIt': 'Got It!',
    
    // AI Tips Modal
    'aiTips.modal.title': 'AI Tips & Insights',
    'aiTips.modal.subtitle': 'Personalized financial advice powered by AI',
    'aiTips.modal.whatIs': 'What Are AI Tips?',
    'aiTips.modal.intro': 'Get personalized financial recommendations multiple times per day. Our AI analyzes your spending patterns and finds opportunities to save money automatically.',
    'aiTips.modal.howItWorks': 'How It Works',
    'aiTips.modal.step1.title': 'Continuous Analysis',
    'aiTips.modal.step1.description': 'AI monitors your transactions 24/7, learning your patterns and identifying opportunities.',
    'aiTips.modal.step2.title': 'Smart Detection',
    'aiTips.modal.step2.description': 'Automatically finds duplicate subscriptions, unusual charges, and potential savings.',
    'aiTips.modal.step3.title': 'Personalized Tips',
    'aiTips.modal.step3.description': 'Receive tips tailored to your specific situation, not generic advice.',
    'aiTips.modal.step4.title': 'Track Savings',
    'aiTips.modal.step4.description': 'See how much money you\'ve saved by following AI recommendations.',
    'aiTips.modal.exampleTips': 'Example Tips You Might Get',
    'aiTips.modal.example1.title': 'Duplicate Subscription Alert',
    'aiTips.modal.example1.description': '"You have two music subscriptions (Spotify and Apple Music). Cancel one to save €10/month."',
    'aiTips.modal.example2.title': 'Better Deal Found',
    'aiTips.modal.example2.description': '"Your phone plan costs €45/month. We found a similar plan for €30/month. Save €180/year!"',
    'aiTips.modal.example3.title': 'Spending Pattern Insight',
    'aiTips.modal.example3.description': '"You spend 40% more on weekends. Try meal planning to reduce weekend grocery trips."',
    'aiTips.modal.example4.title': 'Savings Opportunity',
    'aiTips.modal.example4.description': '"You have €200 sitting idle. Move it to savings to earn interest and reach your goal faster."',
    'aiTips.modal.benefits': 'Key Benefits',
    'aiTips.modal.benefit1.title': 'Effortless Savings',
    'aiTips.modal.benefit1.description': 'Save money without thinking about it. AI does the work for you.',
    'aiTips.modal.benefit2.title': 'Personalized',
    'aiTips.modal.benefit2.description': 'Every tip is specifically for you, based on your unique financial situation.',
    'aiTips.modal.benefit3.title': 'Actionable',
    'aiTips.modal.benefit3.description': 'Clear, specific advice you can act on immediately, not vague suggestions.',
    'aiTips.modal.cta': 'Get Your First AI Tip',
    'aiTips.modal.ctaDescription': 'Start receiving personalized money-saving tips today with Azrefy.',
    'aiTips.modal.gotIt': 'Got It!',
    
    // Roast Mode Modal
    'roast.modal.title': 'Roast Mode AI',
    'roast.modal.subtitle': 'Sometimes you need brutal honesty',
    'roast.modal.whatIs': 'What is Roast Mode?',
    'roast.modal.intro': 'Choose from three AI personalities: Professional, Friendly, or Roast Mode. Sometimes tough love and brutal honesty is what you need to fix bad spending habits.',
    'roast.modal.howItWorks': 'How It Works',
    'roast.modal.step1.title': 'Choose Your AI',
    'roast.modal.step1.description': 'Select from Professional (formal), Friendly (encouraging), or Roast Mode (brutally honest).',
    'roast.modal.step2.title': 'AI Analyzes',
    'roast.modal.step2.description': 'Your chosen AI personality reviews your spending patterns and habits.',
    'roast.modal.step3.title': 'Get Feedback',
    'roast.modal.step3.description': 'Receive tips and insights delivered in your chosen AI\'s unique voice and style.',
    'roast.modal.step4.title': 'Switch Anytime',
    'roast.modal.step4.description': 'Not feeling the roast today? Switch to Friendly mode instantly in settings.',
    'roast.modal.exampleRoasts': 'Example Roasts',
    'roast.modal.example1': 'Another coffee? That\'s your third one today. At this rate, you\'re basically funding a coffee plantation. Try brewing at home and save €200/month.',
    'roast.modal.example2': 'You set a budget of €500 for shopping but spent €750. Did the budget mean nothing to you, or can you just not count?',
    'roast.modal.example3': 'Five food delivery orders this week? Your kitchen misses you. It\'s been so long, it\'s getting dusty in there.',
    'roast.modal.example4': 'You saved €0 this month. Your future self is going to have some words for your present self. Not nice words.',
    'roast.modal.warning': 'Important Note',
    'roast.modal.warningText': 'Roast Mode uses humor and sarcasm to deliver honest feedback about spending habits. It\'s designed to be motivational through accountability, not to be hurtful. All feedback includes actionable advice.',
    'roast.modal.switchBack': 'You can always switch back to Friendly or Professional mode if Roast Mode isn\'t for you.',
    'roast.modal.whyUseful': 'Why Use Roast Mode?',
    'roast.modal.benefit1.title': 'Accountability',
    'roast.modal.benefit1.description': 'Sometimes gentle suggestions don\'t work. Roast Mode holds you accountable.',
    'roast.modal.benefit2.title': 'Memorable',
    'roast.modal.benefit2.description': 'Funny, sarcastic tips stick in your mind better than boring advice.',
    'roast.modal.benefit3.title': 'Real Talk',
    'roast.modal.benefit3.description': 'No sugar-coating. Get the honest truth about your spending habits.',
    'roast.modal.cta': 'Ready for Some Tough Love?',
    'roast.modal.ctaDescription': 'Enable Roast Mode and let our AI keep you accountable with humor and honesty.',
    'roast.modal.gotIt': 'Got It!',
    
    // Check-In Modal
    'checkIn.modal.title': 'Financial Health Check-In',
    'checkIn.modal.subtitle': 'Weekly wellness for your finances',
    'checkIn.modal.whatIs': 'What is Check-In?',
    'checkIn.modal.intro': 'Quick 2-minute weekly sessions where you reflect on your financial situation, set intentions, and track consistency. Build lasting habits one week at a time.',
    'checkIn.modal.howItWorks': 'How It Works',
    'checkIn.modal.step1.title': 'Weekly Prompt',
    'checkIn.modal.step1.description': 'Every Sunday, receive a notification reminder to complete your check-in.',
    'checkIn.modal.step2.title': 'Answer Questions',
    'checkIn.modal.step2.description': 'Simple 4-step wizard asks about your mood, goals, wins, and challenges.',
    'checkIn.modal.step3.title': 'Track Your Streak',
    'checkIn.modal.step3.description': 'Build consistency by completing check-ins every week and watch your streak grow.',
    'checkIn.modal.step4.title': 'Get Insights',
    'checkIn.modal.step4.description': 'Review your past check-ins to see patterns in your financial wellness journey.',
    'checkIn.modal.moodOptions': 'Mood Tracking',
    'checkIn.modal.mood1.title': 'Feeling Good',
    'checkIn.modal.mood1.description': 'Finances are under control, feeling confident and positive.',
    'checkIn.modal.mood2.title': 'Feeling Okay',
    'checkIn.modal.mood2.description': 'Not great, not terrible. Managing but could be better.',
    'checkIn.modal.mood3.title': 'Feeling Stressed',
    'checkIn.modal.mood3.description': 'Worried about money, feel overwhelmed or anxious.',
    'checkIn.modal.questions': 'Weekly Questions',
    'checkIn.modal.question1': 'How do you feel about your finances this week?',
    'checkIn.modal.question2': 'What financial win did you have this week, big or small?',
    'checkIn.modal.question3': 'What\'s your biggest financial challenge right now?',
    'checkIn.modal.question4': 'What\'s one money goal for next week?',
    'checkIn.modal.question5': 'Any money habits you want to track or change?',
    'checkIn.modal.benefits': 'Why Check-Ins Matter',
    'checkIn.modal.benefit1.title': 'Self-Awareness',
    'checkIn.modal.benefit1.description': 'Regular reflection helps you understand your money habits and emotions.',
    'checkIn.modal.benefit2.title': 'Consistency',
    'checkIn.modal.benefit2.description': 'Weekly check-ins build accountability and keep you on track.',
    'checkIn.modal.benefit3.title': 'Progress Tracking',
    'checkIn.modal.benefit3.description': 'Look back at past check-ins to see how far you\'ve come.',
    'checkIn.modal.cta': 'Start Your Check-In Streak',
    'checkIn.modal.ctaDescription': 'Build better financial habits with weekly check-ins. Takes just 2 minutes.',
    'checkIn.modal.gotIt': 'Got It!',
    
    // Points/Rewards Modal
    'points.modal.title': 'AzrefyPoints Rewards Program',
    'points.modal.subtitle': 'Earn rewards for saving money',
    'points.modal.whatIs': 'What Are AzrefyPoints?',
    'points.modal.intro': 'Our gamified rewards system gives you points for every €10 you save. Progress through tiers for better rewards and exchange points for premium features or real-world perks.',
    'points.modal.howToEarn': 'How to Earn Points',
    'points.modal.earn1.title': 'Save Money',
    'points.modal.earn1.points': '1 point per €10',
    'points.modal.earn1.description': 'Earn 1 AzrefyPoint for every €10 you save toward your goals.',
    'points.modal.earn2.title': 'Reach Goals',
    'points.modal.earn2.points': '100-1000 bonus',
    'points.modal.earn2.description': 'Complete savings goals to earn bonus points based on goal amount.',
    'points.modal.earn3.title': 'Maintain Streaks',
    'points.modal.earn3.points': '50 points/week',
    'points.modal.earn3.description': 'Save consistently each week to earn streak bonuses.',
    'points.modal.earn4.title': 'Complete Challenges',
    'points.modal.earn4.points': '25-500 points',
    'points.modal.earn4.description': 'Special monthly challenges offer extra points for specific achievements.',
    'points.modal.levels': 'Tier System',
    'points.modal.level1.title': 'Starter',
    'points.modal.level1.description': '1.0× multiplier',
    'points.modal.level2.title': 'Bronze',
    'points.modal.level2.description': '1.5× multiplier',
    'points.modal.level3.title': 'Silver',
    'points.modal.level3.description': '2.0× multiplier',
    'points.modal.level4.title': 'Gold',
    'points.modal.level4.description': '2.5× multiplier',
    'points.modal.level5.title': 'Platinum',
    'points.modal.level5.description': '3.0× multiplier',
    'points.modal.pointsLabel': 'points',
    'points.modal.rewards': 'What You Can Get',
    'points.modal.reward1.title': 'Premium Features',
    'points.modal.reward1.description': 'Unlock advanced analytics, custom categories, and priority support.',
    'points.modal.reward2.title': 'Gift Cards',
    'points.modal.reward2.description': 'Exchange points for gift cards from popular retailers and brands.',
    'points.modal.reward3.title': 'Discount Codes',
    'points.modal.reward3.description': 'Get exclusive discount codes for trending shopping sites and services.',
    'points.modal.cta': 'Start Earning Points',
    'points.modal.ctaDescription': 'Join AzrefyPoints today and turn your savings into rewards.',
    'points.modal.gotIt': 'Got It!',
    
    'showcase.checkin.badge': 'Weekly Wellness',
    'showcase.checkin.title': 'Financial check-ins.',
    'showcase.checkin.titleGradient': 'Stay consistent.',
    'showcase.checkin.description': 'Quick 2-minute weekly check-ins keep you accountable. Answer simple questions, track your streak, and build lasting financial habits.',
    'showcase.checkin.feature1': 'Simple 4-step weekly wizard',
    'showcase.checkin.showDetails': 'Show More Details',
    'showcase.checkin.feature2': 'Track your consistency streak',
    'showcase.checkin.feature3': 'Mood & goal tracking',
    'showcase.checkin.feature4': 'Get health score boosts',
    
    // Features
    'features.label': 'Why Azrefy?',
    'features.title': 'Why Azrefy?',
    'features.titleGradient': 'Your financial freedom starts here',
    'features.description': 'Not a bank — just the smartest way to see and manage all your accounts in one place.',
    
    'features.multiBanking.title': 'Multi-Bank Integration',
    'features.multiBanking.description': 'No new bank account needed! Simply connect all your existing German bank accounts via our secure app. Use what you have – we bring everything together.',
    'features.multiBanking.feature1': 'We support all German banks: SK, DB, CB, ING, N26, and many more',
    'features.multiBanking.feature2': 'Secure PSD2-compliant connection',
    'features.multiBanking.feature3': 'No account creation – just connect!',
    
    'features.charts.title': 'Real-Time Charts',
    'features.charts.description': 'Beautiful, interactive charts that visualize your spending in real-time. See instantly where your money is going.',
    'features.charts.feature1': 'Live updates with every transaction',
    'features.charts.feature2': 'Category-based breakdown',
    'features.charts.feature3': 'Trend analysis and forecasts',
    'features.charts.badge': 'Popular',
    
    'features.aiTips.title': 'AI Money-Saving Hints',
    'features.aiTips.description': 'Receive personalized tips from our AI on how to save money and avoid unnecessary expenses.',
    'features.aiTips.feature1': 'Multiple new hints daily',
    'features.aiTips.feature2': 'Duplicate subscription detection',
    'features.aiTips.feature3': 'Spending pattern analysis',
    'features.aiTips.badge': 'Innovation',
    
    'features.savings.title': 'Smart Savings Plans',
    'features.savings.description': 'Intelligent, automated savings plans tailored to your financial situation.',
    'features.savings.feature1': 'AI-generated savings goals',
    'features.savings.feature2': 'Automatic savings tips',
    'features.savings.feature3': 'Milestone tracking',
    
    'features.dispo.title': 'Dispo Kredit Freedom',
    'features.dispo.description': 'Break free from the overdraft cycle. Get AI-powered strategies your bank won\'t tell you about.',
    'features.dispo.feature1': 'Escape plan generation',
    'features.dispo.feature2': 'Early warnings & predictions',
    'features.dispo.feature3': 'Step-by-step guidance',
    
    'features.security.title': 'Bank-Level Security',
    'features.security.description': 'Your data is protected with banking-standard encryption. GDPR-compliant and secure.',
    'features.security.feature1': 'AES-256 encryption',
    'features.security.feature2': 'Biometric authentication',
    'features.security.feature3': 'No password storage',
    
    // How It Works
    'howItWorks.title': 'How It Works',
    
    // About
    'about.title': 'About Us',
    'about.mission': 'Azrefy is a fintech startup born in Freiburg im Breisgau, in the heart of the German Black Forest. We\'re passionate about empowering people to take control of their financial future with intelligent, easy-to-use technology.',
    'about.mission.badge': 'Our Mission',
    'about.whoWeHelp.title': 'Who We Help',
    'about.whoWeHelp.titlePrefix': 'Who We',
    'about.whoWeHelp.titleHighlight': 'Help',
    'about.whoWeHelp.subtitle': 'We\'re building Azrefy for everyone in Germany who wants to master their finances — especially those trapped in the Dispo Kredit cycle.',
    'about.whoWeHelp.item1': 'People stuck in Dispo Kredit who can\'t get real help from their bank',
    'about.whoWeHelp.item2': 'Young professionals managing their first salaries',
    'about.whoWeHelp.item3': 'Freelancers with irregular income',
    'about.whoWeHelp.item4': 'Anyone wanting AI-powered insights to reduce spending',
    'about.whoWeHelp.item5': 'Savings-focused individuals looking for smart plans',
    'about.whoWeHelp.item6': 'Users who want all their accounts in one place',
    
    // Waitlist
    'waitlist.title': 'Join the Waitlist',
    'waitlist.description': 'Be the first to know when Azrefy launches',
    'waitlist.email': 'Email Address',
    'waitlist.submit': 'Join Now',
    
    // FAQ
    'faq.label': 'Frequently Asked Questions',
    'faq.title': 'Questions?',
    'faq.titleGradient': "We've got answers.",
    'faq.description': "Find answers to common questions about Azrefy's security, features, and availability.",
    'faq.q1.question': 'Is Azrefy secure and trustworthy?',
    'faq.q1.answer': 'Security is our top priority. Azrefy is licensed by BaFin (German Federal Financial Supervisory Authority) as an Account Information Service, guaranteeing the highest security standards. We use bank-grade encryption to protect your data, and our infrastructure complies with strict German and EU data protection regulations. Your financial information is safeguarded with the same technology that banks use.',
    'faq.q2.question': 'Who can access my financial data?',
    'faq.q2.answer': 'Only you have access to your data. Azrefy uses end-to-end encryption with a zero-knowledge architecture, meaning even we cannot see your private financial information. We never sell, share, or monetize your data with third parties. Your privacy is protected by German law and GDPR compliance.',
    'faq.q3.question': 'Is Azrefy permanently free?',
    'faq.q3.answer': 'Yes, Azrefy is permanently free in its standard version with no hidden fees or charges. You can connect your accounts, analyze contracts, and manage your finances at no cost. We offer an optional premium subscription for users who want advanced features like priority support, extended analytics, and exclusive AI-powered insights.',
    'faq.q4.question': 'What can I accomplish with Azrefy?',
    'faq.q4.answer': 'Azrefy gives you a comprehensive overview of your finances in one place. The app automatically detects your subscriptions and contracts, helps you save money through intelligent analysis, enables one-tap contract cancellation, and provides a digital household budget tracker. Our AI identifies saving opportunities and alerts you to unusual spending patterns.',
    'faq.q5.question': 'Can I connect all my bank accounts?',
    'faq.q5.answer': 'Azrefy supports connections to over 3,000+ banks and financial institutions in Germany. This includes checking accounts, credit cards, investment portfolios, crypto exchanges, and PayPal. We work with all major German banks including SK, VB, DB, CB, N26, and many more.',
    'faq.q6.question': 'Where is Azrefy available?',
    'faq.q6.answer': 'Azrefy is currently available exclusively for users in Germany, ensuring full compliance with German financial regulations and BaFin licensing requirements. You can download the app from the Apple App Store or Google Play Store. We are planning expansion to other European markets in the future.',
    'faq.q7.question': 'Is Azrefy available on desktop computers?',
    'faq.q7.answer': 'The desktop version of Azrefy is currently available exclusively in the App Store for Macs with Apple Silicon (M-series chips). Azrefy is primarily optimized as a mobile app for your smartphone, providing the best experience for on-the-go financial management. All your data syncs seamlessly across devices.',
    
    // Footer
    'footer.tagline': 'Smart Finance Management for Everyone',
    'footer.product': 'Product',
    'footer.features': 'Features',
    'footer.security': 'Security',
    'footer.pricing': 'Pricing',
    'footer.company': 'Company',
    'footer.about': 'About',
    'footer.contact': 'Contact',
    'footer.legal': 'Legal',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.imprint': 'Imprint',
    'footer.rights': 'All rights reserved.',
  },
  de: {
    // Navbar
    'nav.whyAzrefy': 'Warum Azrefy?',
    'nav.features': 'Funktionen',
    'nav.features.dispoPrevent': 'Dispo-Prävention',
    'nav.features.charts': 'Diagramme & Analysen',
    'nav.features.aiTips': 'KI-Tipps',
    'nav.features.roastMode': 'Roast-Modus',
    'nav.features.habitsScore': 'Geld-Gewohnheiten Score',
    'nav.features.checkIn': 'Finanz-Check-In',
    'nav.features.points': 'AzrefyPoints',
    'nav.powerfulFeatures': 'Funktionen',
    'nav.dispoEscape': 'DispoEscape',
    'nav.howItWorks': 'So funktioniert es',
    'nav.about': 'Über uns',
    'nav.contact': 'Kontakt',
    'nav.joinWaitlist': 'Warteliste beitreten',
    
    // Feature Navigation
    'features.navigation.previous': 'Vorheriges Feature',
    'features.navigation.next': 'Nächstes Feature',
    'features.navigation.progress': 'Feature {current} von {total}',
    
    // Hero
    'hero.launchingSoon': 'Bald in Deutschland verfügbar',
    'hero.badge.free': '100% Kostenlos für immer',
    'hero.badge.banks': 'Alle deutschen Banken',
    'hero.badge.ai': 'KI-gestützte Einblicke',
    'hero.title.line1Start': 'Deine ',
    'hero.title.line1AI': 'KI',
    'hero.title.line1End': ' Persönliche Finanzberatung.',
    'hero.title.line2': 'Eine smarte App.',
    'hero.title.line3': 'Echtzeit-',
    'hero.title.line3AI': 'KI',
    'hero.title.line3End': '-Einblicke.',
    'hero.description': 'Verbinde dein bestehendes deutsches Bankkonto über unsere sichere App. Sieh wunderschöne Echtzeit-Charts, erhalte KI-gestützte Geldspartipps, und bleibe finanziell sicher.',
    'hero.banksSupport': 'Wir unterstützen alle deutschen Banken:',
    'hero.joinWaitlist': 'Warteliste beitreten',
    'hero.stats.banks': 'Banken',
    'hero.stats.realtime': 'Echtzeit',
    'hero.stats.updates': 'Updates',
    'hero.stats.bankLevel': 'Bank-Level',
    'hero.stats.security': 'Sicherheit',
    
    // App Showcase
    'showcase.dispo.badge': 'Befreie dich',
    'showcase.dispo.title': 'Raus aus dem Dispo Kredit.',
    'showcase.dispo.titleGradient': 'Bleib in Kontrolle.',
    'showcase.dispo.description': 'Kommst du nicht aus deinem Dispo Kredit raus? Bankberater bietet nur mehr Kredit an? Mit Azrefys KI-Tipps führen wir dich Schritt für Schritt aus diesem Kreislauf heraus.',
    'showcase.dispo.callout.title': 'Egal was du versuchst...',
    'showcase.dispo.callout.text': 'Du schaffst es nicht, aus deinem "Dispo Kredit" rauszukommen, und dein Bankberater gibt dir keine Lösung außer mehr Kredit aufzunehmen? Keine Sorge — mit unseren Azrefy-Tipps führen wir dich Schritt für Schritt aus diesem Kreislauf. Unsere KI analysiert deine Ausgabenmuster und bietet personalisierte Strategien, um dauerhaft aus der Überziehungsfalle zu entkommen.',
    'showcase.dispo.feature1': 'KI-gesteuerte Ausstiegsstrategien',
    'showcase.dispo.feature2': 'Kontostands-Vorhersage & Warnungen',
    'showcase.dispo.feature3': 'Personalisierte Aktionspläne',
    'showcase.dispo.showDetails': 'Mehr Details anzeigen',
    
    // Dispo Escape Section
    'dispoEscape.cta': 'Jetzt Starten',
    
    // App Download Modal
    'appModal.title': 'App kommt bald!',
    'appModal.subtitle': 'Wir geben Azrefy den letzten Schliff',
    'appModal.launchInfo': 'Unsere mobile App befindet sich derzeit in der finalen Entwicklung und wird bald starten. Tritt unserer Warteliste bei, um benachrichtigt zu werden, sobald wir live gehen!',
    'appModal.launchTimeline': 'Erwarteter Start',
    'appModal.launchDate': 'Frühling 2026',
    'appModal.platforms': 'Verfügbar für iOS & Android',
    'appModal.platformsDescription': 'Azrefy wird beim Start sowohl für iPhone als auch für Android-Geräte verfügbar sein.',
    'appModal.joinWaitlist': 'Warteliste für Frühen Zugang beitreten',
    'appModal.joinDescription': 'Sei unter den Ersten, die Azrefy erleben. Erhalte exklusiven frühen Zugang und spezielle Start-Boni.',
    'appModal.notifyMe': 'Benachrichtige mich beim Start',
    'appModal.earlyAccess': 'Vorteile des frühen Zugangs',
    'appModal.benefit1': 'Erster Zugang zur App vor dem öffentlichen Start',
    'appModal.benefit2': 'Exklusive Start-Boni und AzrefyPoints',
    'appModal.benefit3': 'Prioritäts-Support während des ersten Monats',
    'appModal.gotIt': 'Verstanden!',
    
    'showcase.charts.badge': 'Schöne Analysen',
    'showcase.charts.title': 'Alles sehen.',
    'showcase.charts.titleGradient': 'Alles verstehen.',
    'showcase.charts.description': 'Schöne, interaktive Charts, die genau zeigen, wohin dein Geld fließt. Verfolge Ausgaben nach Kategorie, sieh Trends im Zeitverlauf und erhalte wichtige Einblicke.',
    'showcase.charts.feature1': 'Echtzeit-Ausgabencharts',
    'showcase.charts.feature2': 'Kategoriebasierte Aufschlüsselung',
    'showcase.charts.feature3': 'Trendanalyse & Prognosen',
    'showcase.charts.showDetails': 'Mehr Details anzeigen',
    
    'showcase.tips.badge': 'KI-Powered',
    'showcase.tips.title': 'Smartes Geld.',
    'showcase.tips.titleGradient': 'Smarteres Sparen.',
    'showcase.tips.description': 'Erhalte mehrmals täglich personalisierte KI-Tipps. Entdecke doppelte Abonnements, finde bessere Angebote und lerne mühelos Geld zu sparen.',
    'showcase.tips.feature1': 'Tägliche KI-gestützte Geldtipps',
    'showcase.tips.feature2': 'Erkennung doppelter Abonnements',
    'showcase.tips.showDetails': 'Mehr Details anzeigen',
    'showcase.tips.feature3': 'Personalisierte Sparziele',
    
    'showcase.points.badge': 'Prämienprogramm',
    'showcase.points.title': 'Prämien verdienen.',
    'showcase.points.titleGradient': 'Smarter sparen.',
    'showcase.points.description': 'Erhalte AzrefyPoints für je €10 die du sparst. Steige durch Bronze-, Silber- und Gold-Stufen auf für bessere Multiplikatoren. Tausche Punkte gegen Premium-Funktionen, Geschenkgutscheine oder Rabattcodes von beliebten Online-Shops.',
    'showcase.points.feature1': 'Verdiene 1 Punkt pro €10 gespart',
    'showcase.points.feature2': 'Stufen-Multiplikatoren bis zu 3×',
    'showcase.points.feature3': 'Ziel-Meilenstein-Boni bis zu 1.000 Pkt',
    'showcase.points.showDetails': 'Mehr Details anzeigen',
    'showcase.points.feature4': 'Eintausch gegen Premium, Gutscheine & Rabatte',
    
    'showcase.roast.badge': 'Brutal ehrlich',
    'showcase.roast.title': 'KI die "roastet".',
    'showcase.roast.titleGradient': 'Weil du es brauchst.',
    'showcase.roast.description': 'Wähle deine KI-Persönlichkeit: Professionell, Freundlich oder Roast-Modus. Manchmal braucht man harte Wahrheiten, um schlechte Ausgabengewohnheiten zu ändern.',
    'showcase.roast.feature1': 'Drei KI-Persönlichkeiten zur Auswahl',
    'showcase.roast.feature2': 'Roast-Modus deckt schlechte Gewohnheiten auf',
    'showcase.roast.feature3': 'Klare Ansagen mit umsetzbaren Einblicken',
    'showcase.roast.showDetails': 'Mehr Details anzeigen',
    'showcase.roast.feature4': 'Jederzeit wechseln je nach Stimmung',
    
    'showcase.habits.badge': 'Score-basiert',
    'showcase.habits.title': 'Kenne deinen Score.',
    'showcase.habits.titleGradient': 'Verbessere deine Gewohnheiten.',
    'showcase.habits.description': 'Erhalte einen Echtzeit Money Habits Score von 0-100. Verfolge deine finanzielle Gesundheit in den Bereichen Sparen, Ausgabenkontrolle, Budgetierung und Planung.',
    'showcase.habits.feature1': 'Live-Score täglich aktualisiert (0-100)',
    'showcase.habits.feature2': 'Vier Schlüsselkategorien verfolgt',
    'showcase.habits.feature3': 'Erfolgs-Badges & Meilensteine',
    'showcase.habits.feature4': 'Personalisierte Verbesserungstipps',
    'showcase.habits.showDetails': 'Mehr Details anzeigen',
    
    // Habits Score Modal
    'habitsScore.modal.title': 'Money Habits Score erklärt',
    'habitsScore.modal.subtitle': 'So berechnen wir deinen finanziellen Gesundheits-Score',
    'habitsScore.modal.whatIs': 'Was ist der Money Habits Score?',
    'habitsScore.modal.intro': 'Dein Money Habits Score ist eine umfassende 0-100 Punkte Bewertung, die deine finanzielle Gesundheit in vier Schlüsselbereichen evaluiert. Täglich aktualisiert hilft er dir, Fortschritte zu verfolgen und bessere Geldgewohnheiten aufzubauen.',
    'habitsScore.modal.calculation': 'Berechnung',
    'habitsScore.modal.points': 'Punkte',
    'habitsScore.modal.savingRate.title': 'Sparquote',
    'habitsScore.modal.savingRate.description': 'Misst, wie viel deines Einkommens du monatlich sparst.',
    'habitsScore.modal.savingRate.target': 'Ziel',
    'habitsScore.modal.savingRate.formula': 'Score = (Ersparnis / Einkommen) × 200',
    'habitsScore.modal.budgetAdherence.title': 'Budget-Einhaltung',
    'habitsScore.modal.budgetAdherence.description': 'Bewertet, wie gut du dich an dein Budget hältst.',
    'habitsScore.modal.budgetAdherence.optimal': 'Optimaler Bereich',
    'habitsScore.modal.budgetAdherence.good': '70-90% = Volle Punkte',
    'habitsScore.modal.budgetAdherence.overspend': 'Über 100% = Überausgaben',
    'habitsScore.modal.spendingConsistency.title': 'Ausgaben-Konsistenz',
    'habitsScore.modal.spendingConsistency.description': 'Verfolgt die Stabilität deiner Ausgabenmuster von Monat zu Monat.',
    'habitsScore.modal.spendingConsistency.stability': 'Muster-Stabilität',
    'habitsScore.modal.financialHealth.title': 'Finanzielle Gesundheit',
    'habitsScore.modal.financialHealth.description': 'Belohnt positive Kontostände.',
    'habitsScore.modal.financialHealth.positive': 'Positiver Kontostand',
    'habitsScore.modal.distribution': 'Score-Verteilung',
    'habitsScore.modal.totalPoints': 'Gesamt-Punkte',
    'habitsScore.modal.tiers': 'Score-Stufen',
    'habitsScore.modal.excellent': 'Exzellent',
    'habitsScore.modal.good': 'Gut',
    'habitsScore.modal.fair': 'Ordentlich',
    'habitsScore.modal.needsWork': 'Verbesserungswürdig',
    'habitsScore.modal.features': 'In-App Features',
    'habitsScore.modal.overview': 'Übersicht-Tab',
    'habitsScore.modal.overviewFeature1': 'Echtzeit-Score mit motivierenden Nachrichten',
    'habitsScore.modal.overviewFeature2': 'Schnellstatistik-Dashboard',
    'habitsScore.modal.overviewFeature3': 'Personalisierte Verbesserungstipps',
    'habitsScore.modal.breakdown': 'Aufschlüsselung-Tab',
    'habitsScore.modal.breakdownFeature1': 'Detaillierte Komponenten-Analyse',
    'habitsScore.modal.breakdownFeature2': 'Interaktives Kreisdiagramm',
    'habitsScore.modal.breakdownFeature3': 'Ziel vs. Ist-Vergleich',
    'habitsScore.modal.history': 'Verlauf-Tab',
    'habitsScore.modal.historyFeature1': '6-Monats-Trend-Liniendiagramm',
    'habitsScore.modal.historyFeature2': 'Monatliche Score-Aufschlüsselung',
    'habitsScore.modal.historyFeature3': 'Fortschrittsverfolgung über Zeit',
    'habitsScore.modal.cta': 'Bereit, deinen Score zu verbessern?',
    'habitsScore.modal.ctaDescription': 'Lade Azrefy herunter und beginne noch heute, deinen Money Habits Score zu verfolgen. Sieh deinen Fortschritt in Echtzeit und erhalte personalisierte Tipps, um deine finanziellen Ziele zu erreichen.',
    'habitsScore.modal.gotIt': 'Verstanden!',
    
    // Dispo Prevention Modal (German)
    'dispo.modal.title': 'Dispo-Präventions-System',
    'dispo.modal.subtitle': 'So helfen wir dir, Überziehungsgebühren zu vermeiden',
    'dispo.modal.whatIs': 'Was ist Dispo-Prävention?',
    'dispo.modal.intro': 'Unser KI-gestütztes System prognostiziert, wann du ins Minus rutschen könntest und hilft dir, teure Gebühren zu vermeiden, bevor sie entstehen.',
    'dispo.modal.howItWorks': 'So funktioniert es',
    'dispo.modal.step1.title': 'Muster-Analyse',
    'dispo.modal.step1.description': 'KI lernt deine Ausgabenmuster und Einkommenszyklen, um zukünftigenKonto stand genau vorherzusagen.',
    'dispo.modal.step2.title': 'Frühwarnungen',
    'dispo.modal.step2.description': 'Erhalte Warnungen 3-7 Tage bevor dein Kontostand negativ werden könnte – Zeit zum Handeln.',
    'dispo.modal.step3.title': 'Intelligente Vorschläge',
    'dispo.modal.step3.description': 'Personalisierte Empfehlungen zur Dispo-Vermeidung, von Kaufverschiebungen bis zu Zusatzeinkommen.',
    'dispo.modal.step4.title': 'Aktionspläne',
    'dispo.modal.step4.description': 'Schritt-für-Schritt-Anleitungen, um aus bestehenden Dispo-Situationen herauszukommen und ein Sicherheitspolster aufzubauen.',
    'dispo.modal.whyCostly': 'Warum Überziehung so teuer ist',
    'dispo.modal.cost1.title': 'Hohe Zinssätze',
    'dispo.modal.cost1.description': 'Typische Dispo-Zinsen liegen bei 8-15% p.a., das summiert sich schnell.',
    'dispo.modal.cost2.title': 'Tägliche Gebühren',
    'dispo.modal.cost2.description': 'Manche Banken berechnen pro Tag im Minus, was kurzfristige Überziehungen sehr teuer macht.',
    'dispo.modal.cost3.title': 'Kreislauf-Falle',
    'dispo.modal.cost3.description': 'Einmal überzogen ist es schwer rauszukommen, da neue Gebühren dich tiefer ins Minus treiben.',
    'dispo.modal.aiHelps': 'So hilft unsere KI',
    'dispo.modal.feature1.title': 'Kontostand-Prognose',
    'dispo.modal.feature1.description': 'Prognostiziert deinen Kontostand bis zu 30 Tage im Voraus basierend auf wiederkehrenden Zahlungen und Gewohnheiten.',
    'dispo.modal.feature2.title': 'Ausgaben-Warnungen',
    'dispo.modal.feature2.description': 'Intelligente Benachrichtigungen, wenn ein Kauf dich ins Minus bringen könnte.',
    'dispo.modal.feature3.title': 'Ausstiegs-Strategien',
    'dispo.modal.feature3.description': 'Maßgeschneiderte Pläne, um Dispo effizient abzubauen bei Aufrechterhaltung wichtiger Ausgaben.',
    'dispo.modal.cta': 'Übernimm heute die Kontrolle',
    'dispo.modal.ctaDescription': 'Schließe dich Tausenden an, die mit Azrefys KI-gestütztem Präventionssystem der Dispo-Falle entkommen sind.',
    'dispo.modal.gotIt': 'Verstanden!',
    
    // Charts & Analytics Modal (German)
    'charts.modal.title': 'Charts & Analysen',
    'charts.modal.subtitle': 'Visualisiere deine Finanzdaten',
    'charts.modal.whatIs': 'Was sind diese Charts?',
    'charts.modal.intro': 'Schöne, interaktive Visualisierungen, die deine Finanzdaten in umsetzbare Erkenntnisse verwandeln. Sieh Muster, Trends und Chancen auf einen Blick.',
    'charts.modal.availableCharts': 'Verfügbare Chart-Typen',
    'charts.modal.pie.title': 'Kreisdiagramme',
    'charts.modal.pie.description': 'Sieh deine Ausgabenverteilung nach Kategorien. Erkenne schnell, wohin das meiste Geld fließt.',
    'charts.modal.pie.example': 'Beispiel: 40% Lebensmittel, 25% Transport, 20% Unterhaltung, 15% Sonstiges',
    'charts.modal.line.title': 'Liniendiagramme',
    'charts.modal.line.description': 'Verfolge Trends über Zeit. Sieh, wie sich Ausgaben, Einkommen und Ersparnisse Monat für Monat entwickeln.',
    'charts.modal.line.example': 'Beispiel: 6-Monats-Ausgaben-Trend zeigt saisonale Muster',
    'charts.modal.bar.title': 'Balkendiagramme',
    'charts.modal.bar.description': 'Vergleiche verschiedene Kategorien oder Zeiträume nebeneinander. Perfekt für monatliche Vergleiche.',
    'charts.modal.bar.example': 'Beispiel: Vergleiche diesen Monat mit letztem Monat über alle Kategorien',
    'charts.modal.trend.title': 'Trend-Analyse',
    'charts.modal.trend.description': 'Automatische Erkennung von Aufwärts- oder Abwärtstrends mit prädiktiven Einblicken.',
    'charts.modal.trend.example': 'Beispiel: "Deine Lebensmittelausgaben stiegen diesen Monat um 15%"',
    'charts.modal.insights': 'Intelligente Einblicke',
    'charts.modal.insight1.title': 'Zeitbasiert',
    'charts.modal.insight1.description': 'Tages-, Wochen-, Monats- oder Jahresansichten um Muster in jedem Zeitrahmen zu erkennen.',
    'charts.modal.insight2.title': 'Kategorie-Tiefenanalyse',
    'charts.modal.insight2.description': 'Detaillierter Einblick in jede Ausgabenkategorie mit Transaktionen und Unterkategorien.',
    'charts.modal.insight3.title': 'Prognosen',
    'charts.modal.insight3.description': 'KI-gestützte Vorhersagen zeigen erwartete Ausgaben für kommende Monate.',
    'charts.modal.customization': 'Anpassungsoptionen',
    'charts.modal.custom1.title': 'Datumsbereich-Filter',
    'charts.modal.custom1.description': 'Wähle einen beliebigen Zeitraum von letzter Woche bis letztem Jahr.',
    'charts.modal.custom2.title': 'Kategorieauswahl',
    'charts.modal.custom2.description': 'Zeige oder verstecke spezifische Kategorien, um dich auf das Wesentliche zu konzentrieren.',
    'charts.modal.custom3.title': 'Export & Teilen',
    'charts.modal.custom3.description': 'Lade Charts als Bilder oder PDFs für deine Unterlagen herunter.',
    'charts.modal.cta': 'Visualisiere deine Finanzen',
    'charts.modal.ctaDescription': 'Schöne Charts und leistungsstarke Analysen warten in der Azrefy App auf dich.',
    'charts.modal.gotIt': 'Verstanden!',
    
    // AI Tips Modal (German)
    'aiTips.modal.title': 'KI-Tipps & Einblicke',
    'aiTips.modal.subtitle': 'Personalisierte Finanzberatung durch KI',
    'aiTips.modal.whatIs': 'Was sind KI-Tipps?',
    'aiTips.modal.intro': 'Erhalte mehrmals täglich personalisierte Finanzempfehlungen. Unsere KI analysiert deine Ausgabenmuster und findet automatisch Sparmöglichkeiten.',
    'aiTips.modal.howItWorks': 'So funktioniert es',
    'aiTips.modal.step1.title': 'Kontinuierliche Analyse',
    'aiTips.modal.step1.description': 'KI überwacht deine Transaktionen 24/7, lernt deine Muster und identifiziert Chancen.',
    'aiTips.modal.step2.title': 'Intelligente Erkennung',
    'aiTips.modal.step2.description': 'Findet automatisch doppelte Abos, ungewöhnliche Abbuchungen und Sparpotenziale.',
    'aiTips.modal.step3.title': 'Personalisierte Tipps',
    'aiTips.modal.step3.description': 'Empfehlungen speziell für deine Situation, keine generischen Ratschläge.',
    'aiTips.modal.step4.title': 'Ersparnisse verfolgen',
    'aiTips.modal.step4.description': 'Sieh, wie viel Geld du durch Befolgen der KI-Empfehlungen gespart hast.',
    'aiTips.modal.exampleTips': 'Beispiel-Tipps die du erhalten könntest',
    'aiTips.modal.example1.title': 'Doppeltes Abo-Alarm',
    'aiTips.modal.example1.description': '"Du hast zwei Musik-Abos (Spotify und Apple Music). Kündige eines, um €10/Monat zu sparen."',
    'aiTips.modal.example2.title': 'Besseres Angebot gefunden',
    'aiTips.modal.example2.description': '"Dein Handytarif kostet €45/Monat. Wir haben einen ähnlichen für €30/Monat gefunden. Spare €180/Jahr!"',
    'aiTips.modal.example3.title': 'Ausgaben-Muster-Einblick',
    'aiTips.modal.example3.description': '"Du gibst am Wochenende 40% mehr aus. Versuche Essensplanung, um Wochenend-Einkäufe zu reduzieren."',
    'aiTips.modal.example4.title': 'Spar-Gelegenheit',
    'aiTips.modal.example4.description': '"Du hast €200 ungenutzt liegen. Verschiebe sie ins Sparkonto für Zinsen und erreiche schneller dein Ziel."',
    'aiTips.modal.benefits': 'Hauptvorteile',
    'aiTips.modal.benefit1.title': 'Müheloses Sparen',
    'aiTips.modal.benefit1.description': 'Spare Geld ohne darüber nachzudenken. KI erledigt die Arbeit für dich.',
    'aiTips.modal.benefit2.title': 'Personalisiert',
    'aiTips.modal.benefit2.description': 'Jeder Tipp ist speziell für dich, basierend auf deiner einzigartigen Finanzsituation.',
    'aiTips.modal.benefit3.title': 'Umsetzbar',
    'aiTips.modal.benefit3.description': 'Klare, spezifische Ratschläge, die du sofort umsetzen kannst, keine vagen Vorschläge.',
    'aiTips.modal.cta': 'Hol dir deinen ersten KI-Tipp',
    'aiTips.modal.ctaDescription': 'Beginne noch heute, personalisierte Geld-Spar-Tipps mit Azrefy zu erhalten.',
    'aiTips.modal.gotIt': 'Verstanden!',
    
    // Roast Mode Modal (German)
    'roast.modal.title': 'Roast-Modus KI',
    'roast.modal.subtitle': 'Manchmal braucht man brutale Ehrlichkeit',
    'roast.modal.whatIs': 'Was ist der Roast-Modus?',
    'roast.modal.intro': 'Wähle zwischen drei KI-Persönlichkeiten: Professionell, Freundlich oder Roast-Modus. Manchmal braucht man harte Wahrheiten, um schlechte Ausgabengewohnheiten zu ändern.',
    'roast.modal.howItWorks': 'So funktioniert es',
    'roast.modal.step1.title': 'Wähle deine KI',
    'roast.modal.step1.description': 'Wähle zwischen Professionell (formell), Freundlich (ermutigend) oder Roast-Modus (brutal ehrlich).',
    'roast.modal.step2.title': 'KI analysiert',
    'roast.modal.step2.description': 'Deine gewählte KI-Persönlichkeit überprüft deine Ausgabenmuster und Gewohnheiten.',
    'roast.modal.step3.title': 'Feedback erhalten',
    'roast.modal.step3.description': 'Empfange Tipps und Einblicke in der einzigartigen Stimme und dem Stil deiner gewählten KI.',
    'roast.modal.step4.title': 'Jederzeit wechseln',
    'roast.modal.step4.description': 'Nicht in Roast-Laune heute? Wechsle sofort zum Freundlich-Modus in den Einstellungen.',
    'roast.modal.exampleRoasts': 'Beispiel-Roasts',
    'roast.modal.example1': 'Noch ein Kaffee? Das ist dein dritter heute. So finanzierst du praktisch eine Kaffeeplantage. Versuch mal Zuhause zu brühen und spare €200/Monat.',
    'roast.modal.example2': 'Du hast ein Budget von €500 für Shopping gesetzt, aber €750 ausgegeben. Bedeutete das Budget nichts für dich, oder kannst du einfach nicht rechnen?',
    'roast.modal.example3': 'Fünf Essenslieferungen diese Woche? Deine Küche vermisst dich. Es ist so lange her, dass es dort staubig wird.',
    'roast.modal.example4': 'Du hast €0 diesen Monat gespart. Dein zukünftiges Ich wird ein paar Worte mit deinem jetzigen Ich haben. Keine netten Worte.',
    'roast.modal.warning': 'Wichtiger Hinweis',
    'roast.modal.warningText': 'Der Roast-Modus nutzt Humor und Sarkasmus, um ehrliches Feedback zu Ausgabengewohnheiten zu geben. Er ist durch Verantwortlichkeit motivierend gestaltet, nicht verletzend. Jedes Feedback enthält umsetzbare Ratschläge.',
    'roast.modal.switchBack': 'Du kannst jederzeit zurück zum Freundlich- oder Professionell-Modus wechseln, wenn Roast-Modus nicht dein Ding ist.',
    'roast.modal.whyUseful': 'Warum Roast-Modus nutzen?',
    'roast.modal.benefit1.title': 'Verantwortlichkeit',
    'roast.modal.benefit1.description': 'Manchmal funktionieren sanfte Vorschläge nicht. Roast-Modus hält dich verantwortlich.',
    'roast.modal.benefit2.title': 'Einprägsam',
    'roast.modal.benefit2.description': 'Lustige, sarkastische Tipps bleiben besser im Gedächtnis als langweilige Ratschläge.',
    'roast.modal.benefit3.title': 'Echte Ansage',
    'roast.modal.benefit3.description': 'Kein Schönreden. Die ehrliche Wahrheit über deine Ausgabengewohnheiten.',
    'roast.modal.cta': 'Bereit für harte Wahrheiten?',
    'roast.modal.ctaDescription': 'Aktiviere den Roast-Modus und lass unsere KI dich mit Humor und Ehrlichkeit auf Kurs halten.',
    'roast.modal.gotIt': 'Verstanden!',
    
    // Check-In Modal (German)
    'checkIn.modal.title': 'Finanzieller Gesundheits-Check-In',
    'checkIn.modal.subtitle': 'Wöchentliches Wellness für deine Finanzen',
    'checkIn.modal.whatIs': 'Was ist Check-In?',
    'checkIn.modal.intro': 'Schnelle 2-Minuten wöchentliche Sessions, in denen du über deine Finanzsituation reflektierst, Absichten setzt und Konsequenz verfolgst. Baue dauerhafte Gewohnheiten Woche für Woche auf.',
    'checkIn.modal.howItWorks': 'So funktioniert es',
    'checkIn.modal.step1.title': 'Wöchentliche Erinnerung',
    'checkIn.modal.step1.description': 'Jeden Sonntag erhältst du eine Benachrichtigungserinnerung, dein Check-In zu vervollständigen.',
    'checkIn.modal.step2.title': 'Fragen beantworten',
    'checkIn.modal.step2.description': 'Einfacher 4-Schritt-Assistent fragt nach deiner Stimmung, Zielen, Erfolgen und Herausforderungen.',
    'checkIn.modal.step3.title': 'Verfolge deinen Streak',
    'checkIn.modal.step3.description': 'Baue Konsistenz auf, indem du jede Woche Check-Ins absolvierst und sieh deinen Streak wachsen.',
    'checkIn.modal.step4.title': 'Erhalte Einblicke',
    'checkIn.modal.step4.description': 'Überprüfe vergangene Check-Ins, um Muster in deiner finanziellen Wellness-Reise zu sehen.',
    'checkIn.modal.moodOptions': 'Stimmungs-Tracking',
    'checkIn.modal.mood1.title': 'Fühle mich gut',
    'checkIn.modal.mood1.description': 'Finanzen unter Kontrolle, fühle mich zuversichtlich und positiv.',
    'checkIn.modal.mood2.title': 'Fühle mich okay',
    'checkIn.modal.mood2.description': 'Weder großartig noch schlecht. Komme klar, könnte aber besser sein.',
    'checkIn.modal.mood3.title': 'Fühle mich gestresst',
    'checkIn.modal.mood3.description': 'Geldsorgen, fühle mich überfordert oder ängstlich.',
    'checkIn.modal.questions': 'Wöchentliche Fragen',
    'checkIn.modal.question1': 'Wie fühlst du dich diese Woche bezüglich deiner Finanzen?',
    'checkIn.modal.question2': 'Welchen finanziellen Erfolg hattest du diese Woche, groß oder klein?',
    'checkIn.modal.question3': 'Was ist deine größte finanzielle Herausforderung gerade?',
    'checkIn.modal.question4': 'Was ist ein Geldziel für nächste Woche?',
    'checkIn.modal.question5': 'Gibt es Geldgewohnheiten, die du verfolgen oder ändern möchtest?',
    'checkIn.modal.benefits': 'Warum Check-Ins wichtig sind',
    'checkIn.modal.benefit1.title': 'Selbstwahrnehmung',
    'checkIn.modal.benefit1.description': 'Regelmäßige Reflexion hilft dir, deine Geldgewohnheiten und Emotionen zu verstehen.',
    'checkIn.modal.benefit2.title': 'Konsequenz',
    'checkIn.modal.benefit2.description': 'Wöchentliche Check-Ins schaffen Verantwortlichkeit und halten dich auf Kurs.',
    'checkIn.modal.benefit3.title': 'Fortschrittsverfolgung',
    'checkIn.modal.benefit3.description': 'Schaue zurück auf vergangene Check-Ins, um zu sehen, wie weit du gekommen bist.',
    'checkIn.modal.cta': 'Starte deinen Check-In-Streak',
    'checkIn.modal.ctaDescription': 'Baue bessere Finanzgewohnheiten mit wöchentlichen Check-Ins auf. Dauert nur 2 Minuten.',
    'checkIn.modal.gotIt': 'Verstanden!',
    
    // Points/Rewards Modal (German)
    'points.modal.title': 'AzrefyPoints Prämienprogramm',
    'points.modal.subtitle': 'Verdiene Prämien fürs Geld sparen',
    'points.modal.whatIs': 'Was sind AzrefyPoints?',
    'points.modal.intro': 'Unser gamifiziertes Prämiensystem gibt dir Punkte für je €10 die du sparst. Steige durch Stufen auf für bessere Prämien und tausche Punkte gegen Premium-Features oder echte Belohnungen.',
    'points.modal.howToEarn': 'So verdienst du Punkte',
    'points.modal.earn1.title': 'Geld sparen',
    'points.modal.earn1.points': '1 Punkt pro €10',
    'points.modal.earn1.description': 'Verdiene 1 AzrefyPoint für je €10 die du zu deinen Zielen sparst.',
    'points.modal.earn2.title': 'Ziele erreichen',
    'points.modal.earn2.points': '100-1000 Bonus',
    'points.modal.earn2.description': 'Vervollständige Sparziele, um Bonuspunkte basierend auf Zielhöhe zu verdienen.',
    'points.modal.earn3.title': 'Streaks aufrechterhalten',
    'points.modal.earn3.points': '50 Punkte/Woche',
    'points.modal.earn3.description': 'Spare konsequent jede Woche, um Streak-Boni zu verdienen.',
    'points.modal.earn4.title': 'Challenges abschließen',
    'points.modal.earn4.points': '25-500 Punkte',
    'points.modal.earn4.description': 'Spezielle monatliche Challenges bieten Extrapunkte für bestimmte Erfolge.',
    'points.modal.levels': 'Stufen-System',
    'points.modal.level1.title': 'Starter',
    'points.modal.level1.description': '1,0× Multiplikator',
    'points.modal.level2.title': 'Bronze',
    'points.modal.level2.description': '1,5× Multiplikator',
    'points.modal.level3.title': 'Silber',
    'points.modal.level3.description': '2,0× Multiplikator',
    'points.modal.level4.title': 'Gold',
    'points.modal.level4.description': '2,5× Multiplikator',
    'points.modal.level5.title': 'Platin',
    'points.modal.level5.description': '3,0× Multiplikator',
    'points.modal.pointsLabel': 'Punkte',
    'points.modal.rewards': 'Was du bekommen kannst',
    'points.modal.reward1.title': 'Premium-Features',
    'points.modal.reward1.description': 'Schalte erweiterte Analysen, benutzerdefinierte Kategorien und Priority Support frei.',
    'points.modal.reward2.title': 'Geschenkgutscheine',
    'points.modal.reward2.description': 'Tausche Punkte gegen Gutscheine von beliebten Händlern und Marken.',
    'points.modal.reward3.title': 'Rabattcodes',
    'points.modal.reward3.description': 'Erhalte exklusive Rabattcodes für angesagte Shopping-Seiten und Dienste.',
    'points.modal.cta': 'Punkte verdienen starten',
    'points.modal.ctaDescription': 'Tritt heute AzrefyPoints bei und verwandle deine Ersparnisse in Prämien.',
    'points.modal.gotIt': 'Verstanden!',
    
    'showcase.checkin.badge': 'Wöchentliches Wellness',
    'showcase.checkin.title': 'Finanzielle Check-ins.',
    'showcase.checkin.titleGradient': 'Bleib konsequent.',
    'showcase.checkin.description': 'Schnelle 2-Minuten wöchentliche Check-ins halten dich verantwortlich. Beantworte einfache Fragen, verfolge deinen Streak und baue dauerhafte finanzielle Gewohnheiten auf.',
    'showcase.checkin.showDetails': 'Mehr Details anzeigen',
    'showcase.checkin.feature1': 'Einfacher 4-Schritt wöchentlicher Wizard',
    'showcase.checkin.feature2': 'Verfolge deinen Konsistenz-Streak',
    'showcase.checkin.feature3': 'Stimmungs- & Zielverfolgung',
    'showcase.checkin.feature4': 'Erhalte Health-Score-Boosts',
    
    // Features
    'features.label': 'Warum Azrefy?',
    'features.title': 'Warum Azrefy?',
    'features.titleGradient': 'Deine finanzielle Freiheit beginnt hier',
    'features.description': 'Keine Bank — nur der smarteste Weg, alle deine Konten an einem Ort zu sehen und zu verwalten.',
    
    'features.multiBanking.title': 'Multi-Bank Integration',
    'features.multiBanking.description': 'Kein neues Bankkonto nötig! Verbinde einfach alle deine bestehenden deutschen Bankkonten über unsere sichere App. Nutze was du hast – wir bringen alles zusammen.',
    'features.multiBanking.feature1': 'Wir unterstützen alle deutschen Banken: SK, DB, CB, ING, N26, und viele mehr',
    'features.multiBanking.feature2': 'Sichere PSD2-konforme Verbindung',
    'features.multiBanking.feature3': 'Keine Kontoerstellung – nur verbinden!',
    
    'features.charts.title': 'Echtzeit-Charts',
    'features.charts.description': 'Wunderschöne, interaktive Diagramme, die deine Ausgaben in Echtzeit visualisieren. Sieh sofort, wohin dein Geld fließt.',
    'features.charts.feature1': 'Live-Updates bei jeder Transaktion',
    'features.charts.feature2': 'Kategoriebasierte Aufschlüsselung',
    'features.charts.feature3': 'Trend-Analyse und Prognosen',
    'features.charts.badge': 'Beliebt',
    
    'features.aiTips.title': 'KI Geldspar-Tipps',
    'features.aiTips.description': 'Erhalte personalisierte Tipps von unserer KI, wie du Geld sparen und unnötige Ausgaben vermeiden kannst.',
    'features.aiTips.feature1': 'Mehrmals täglich neue Hinweise',
    'features.aiTips.feature2': 'Erkennung doppelter Abonnements',
    'features.aiTips.feature3': 'Ausgabenmuster-Analyse',
    'features.aiTips.badge': 'Innovation',
    
    'features.savings.title': 'Intelligente Sparpläne',
    'features.savings.description': 'Intelligente, automatisierte Sparpläne, die auf deine finanzielle Situation zugeschnitten sind.',
    'features.savings.feature1': 'KI-generierte Sparziele',
    'features.savings.feature2': 'Automatische Spartipps',
    'features.savings.feature3': 'Meilenstein-Tracking',
    
    'features.dispo.title': 'Dispo Kredit Freiheit',
    'features.dispo.description': 'Befreie dich aus dem Überziehungskreislauf. Erhalte KI-gestützte Strategien, die dir deine Bank nicht verrät.',
    'features.dispo.feature1': 'Ausstiegsplan-Generierung',
    'features.dispo.feature2': 'Frühwarnungen & Vorhersagen',
    'features.dispo.feature3': 'Schritt-für-Schritt-Anleitung',
    
    'features.security.title': 'Bank-Level Sicherheit',
    'features.security.description': 'Deine Daten sind mit Banking-Standard-Verschlüsselung geschützt. GDPR-konform und sicher.',
    'features.security.feature1': 'AES-256 Verschlüsselung',
    'features.security.feature2': 'Biometrische Authentifizierung',
    'features.security.feature3': 'Keine Speicherung von Passwörtern',
    
    // How It Works
    'howItWorks.title': 'So funktioniert es',
    
    // About
    'about.title': 'Über uns',
    'about.mission': 'Azrefy ist ein Fintech-Startup aus Freiburg im Breisgau, im Herzen des Schwarzwalds. Wir sind leidenschaftlich daran interessiert, Menschen zu befähigen, die Kontrolle über ihre finanzielle Zukunft mit intelligenter, benutzerfreundlicher Technologie zu übernehmen.',
    'about.mission.badge': 'Unsere Mission',
    'about.whoWeHelp.title': 'Wen wir helfen',
    'about.whoWeHelp.titlePrefix': 'Wen wir',
    'about.whoWeHelp.titleHighlight': 'helfen',
    'about.whoWeHelp.subtitle': 'Wir bauen Azrefy für alle in Deutschland, die ihre Finanzen meistern wollen — besonders für diejenigen, die im Dispo Kredit-Kreislauf gefangen sind.',
    'about.whoWeHelp.item1': 'Menschen im Dispo Kredit, die keine echte Hilfe von ihrer Bank bekommen',
    'about.whoWeHelp.item2': 'Junge Berufstätige, die ihr erstes Gehalt verwalten',
    'about.whoWeHelp.item3': 'Freelancer mit unregelmäßigem Einkommen',
    'about.whoWeHelp.item4': 'Alle, die KI-gestützte Einblicke zur Ausgabenreduzierung wollen',
    'about.whoWeHelp.item5': 'Sparfokussierte Menschen auf der Suche nach intelligenten Plänen',
    'about.whoWeHelp.item6': 'Nutzer, die alle ihre Konten an einem Ort haben wollen',
    
    // Waitlist
    'waitlist.title': 'Warteliste beitreten',
    'waitlist.description': 'Sei der Erste, der erfährt, wann Azrefy startet',
    'waitlist.email': 'E-Mail-Adresse',
    'waitlist.submit': 'Jetzt beitreten',
    
    // FAQ
    'faq.label': 'Häufig gestellte Fragen',
    'faq.title': 'Fragen?',
    'faq.titleGradient': 'Wir haben Antworten.',
    'faq.description': 'Finde Antworten auf häufige Fragen zur Sicherheit, den Funktionen und der Verfügbarkeit von Azrefy.',
    'faq.q1.question': 'Ist Azrefy sicher und vertrauenswürdig?',
    'faq.q1.answer': 'Sicherheit steht bei Azrefy an oberster Stelle. Azrefy besitzt eine Lizenz der BaFin (Bundesanstalt für Finanzdienstleistungsaufsicht) als Kontoinformationsdienst, was höchste Sicherheitsstandards garantiert. Wir nutzen bankübliche Verschlüsselungstechnologie zum Schutz Deiner Daten, und unsere Infrastruktur erfüllt die strengen deutschen und EU-Datenschutzbestimmungen. Deine Finanzinformationen werden mit derselben Technologie geschützt, die auch Banken verwenden.',
    'faq.q2.question': 'Wer kann auf meine Finanzdaten zugreifen?',
    'faq.q2.answer': 'Nur Du hast Zugriff auf Deine Daten. Azrefy nutzt eine Ende-zu-Ende-Verschlüsselung mit Zero-Knowledge-Architektur, sodass selbst wir Deine privaten Finanzinformationen nicht einsehen können. Wir verkaufen, teilen oder monetarisieren Deine Daten niemals mit Dritten. Deine Privatsphäre ist durch deutsches Recht und DSGVO-Konformität geschützt.',
    'faq.q3.question': 'Ist Azrefy dauerhaft kostenlos?',
    'faq.q3.answer': 'Ja, Azrefy ist in der Standard-Version dauerhaft kostenlos ohne versteckte Gebühren oder Kosten. Du kannst Deine Konten verbinden, Verträge analysieren und Deine Finanzen verwalten – komplett kostenfrei. Für Nutzer, die erweiterte Funktionen wie Priority-Support, erweiterte Analysen und exklusive KI-gestützte Einblicke wünschen, bieten wir ein optionales Premium-Abonnement an.',
    'faq.q4.question': 'Was kann ich mit Azrefy erreichen?',
    'faq.q4.answer': 'Azrefy bietet Dir einen umfassenden Überblick über Deine Finanzen an einem Ort. Die App erkennt automatisch Deine Abonnements und Verträge, hilft Dir durch intelligente Analysen beim Sparen, ermöglicht das Kündigen von Verträgen per Fingertipp und bietet ein digitales Haushaltsbuch. Unsere KI identifiziert Sparmöglichkeiten und warnt Dich vor ungewöhnlichen Ausgabenmustern.',
    'faq.q5.question': 'Kann ich alle meine Bankkonten verbinden?',
    'faq.q5.answer': 'Azrefy unterstützt die Anbindung von über 3.000 Banken und Finanzdienstleistern in Deutschland. Dazu gehören Girokonten, Kreditkarten, Depots, Krypto-Börsen und PayPal. Wir arbeiten mit allen großen deutschen Banken zusammen, darunter SK, VB, DB, CB, N26 und viele mehr.',
    'faq.q6.question': 'Wo ist Azrefy verfügbar?',
    'faq.q6.answer': 'Azrefy ist aktuell exklusiv für Nutzer in Deutschland verfügbar und gewährleistet damit vollständige Konformität mit deutschen Finanzvorschriften und BaFin-Lizenzierungsanforderungen. Du kannst die App ganz einfach im Apple App Store oder im Google Play Store herunterladen. Eine Expansion in andere europäische Märkte ist für die Zukunft geplant.',
    'faq.q7.question': 'Ist Azrefy auf Desktop-Computern verfügbar?',
    'faq.q7.answer': 'Die Desktop-Version von Azrefy ist derzeit ausschließlich im App Store für Macs mit Apple Silicon (M-Serie Chips) verfügbar. Azrefy ist primär als mobile App für Dein Smartphone optimiert und bietet die beste Erfahrung für Finanzmanagement unterwegs. Alle Deine Daten synchronisieren sich nahtlos über alle Geräte hinweg.',
    
    // Footer
    'footer.tagline': 'Smartes Finanzmanagement für jeden',
    'footer.product': 'Produkt',
    'footer.features': 'Funktionen',
    'footer.security': 'Sicherheit',
    'footer.pricing': 'Preise',
    'footer.company': 'Unternehmen',
    'footer.about': 'Über uns',
    'footer.contact': 'Kontakt',
    'footer.legal': 'Rechtliches',
    'footer.privacy': 'Datenschutz',
    'footer.terms': 'AGB',
    'footer.imprint': 'Impressum',
    'footer.rights': 'Alle Rechte vorbehalten.',
  },
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('azrefy-language')
    return (saved === 'en' || saved === 'de' ? saved : 'de') as Language
  })

  useEffect(() => {
    localStorage.setItem('azrefy-language', language)
  }, [language])

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
  }

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
